import { z } from 'zod';
import { IdSchema, PaginationQuerySchema, paginatedSchema } from './common';
import { IvaConditionSchema, TaxIdTypeSchema, taxIdField } from './fiscal';

export const COUNTERPARTY_KINDS = ['SUPPLIER', 'CUSTOMER', 'BOTH'] as const;
export const CounterpartyKindSchema = z.enum(COUNTERPARTY_KINDS);
export type CounterpartyKind = z.infer<typeof CounterpartyKindSchema>;

export const CounterpartySchema = z.object({
  id: IdSchema,
  name: z.string(),
  kind: CounterpartyKindSchema,
  /**
   * How this counterparty is identified in its country: `CUIT`, `DNI`, `RUT`.
   *
   * The legal set is `CountryProfile.identity.types`, not a list compiled into
   * this package — a workspace in Chile has no CUIT to offer.
   */
  taxIdType: TaxIdTypeSchema,
  /** Stored without separators, e.g. `20123456786`. */
  taxId: z.string().nullable(),
  /**
   * The identifier as the country prints it, e.g. `20-12345678-6`.
   *
   * Formatted by the server, because the format is the country's and the
   * client has no way to know it: Argentina groups a CUIT as 2-8-1, Chile
   * punctuates a RUT with a dot and a hyphen. Falls back to `taxId` when the
   * country has no format or the stored value is not well formed.
   */
  taxIdFormatted: z.string().nullable(),
  /**
   * The counterparty's standing for the country's tax, as the pack's regime
   * code. Together with the workspace's own regime it decides what a document
   * issued to them looks like.
   */
  ivaCondition: IvaConditionSchema,
  /** Gross-income / turnover tax registration number, where the country has one. */
  grossIncomeNumber: z.string().nullable(),
  /** Jurisdiction the counterparty operates in, as the pack's code. */
  province: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  address: z.string().nullable(),
  contactPerson: z.string().nullable(),
  note: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Counterparty = z.infer<typeof CounterpartySchema>;

export const CounterpartyListSchema = paginatedSchema(CounterpartySchema);

/**
 * Fiscal identity fields, shared by create and update.
 *
 * Neither `taxIdType` nor `ivaCondition` has a default here. Both used to
 * (`CUIT`, `RESPONSABLE_INSCRIPTO`), and both were wrong the moment the
 * product left Argentina: a Chilean supplier would have been filed as a
 * responsable inscripto with a CUIT. The API fills in the workspace's own
 * country defaults from the pack.
 */
const CounterpartyFieldsSchema = z.object({
  name: z.string().trim().min(1).max(300).describe('Razón social o nombre de fantasía.'),
  kind: CounterpartyKindSchema.default('SUPPLIER').describe(
    'Proveedor, cliente, o ambos a la vez.',
  ),
  taxIdType: TaxIdTypeSchema.optional().describe(
    'Tipo de identificación según el país de la empresa. Si no se indica, se usa el del país.',
  ),
  taxId: z
    .string()
    .trim()
    .max(32)
    .nullable()
    .optional()
    .describe(
      'Número sin separadores. Lo valida el país: en Argentina un CUIT se ' +
        'comprueba con su dígito verificador módulo 11, en Chile un RUT con el suyo.',
    ),
  ivaCondition: IvaConditionSchema.optional().describe(
    'Condición frente al impuesto del país. Es la de la contraparte, y junto con ' +
      'la de la empresa decide el comprobante.',
  ),
  grossIncomeNumber: z
    .string()
    .trim()
    .max(30)
    .nullable()
    .optional()
    .describe('Inscripción en el impuesto a los ingresos brutos, donde exista.'),
  province: z
    .string()
    .trim()
    .max(16)
    .nullable()
    .optional()
    .describe('Jurisdicción, por su código del país (una provincia, una región, un departamento).'),
  phone: z.string().trim().max(32).nullable().optional(),
  email: z.string().trim().max(200).nullable().optional(),
  address: z.string().trim().max(500).nullable().optional(),
  contactPerson: z.string().trim().max(200).nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
  isActive: z
    .boolean()
    .default(true)
    .describe('Una contraparte inactiva no se ofrece en los formularios.'),
});

/**
 * Create and update carry the identity exactly as typed.
 *
 * Validation and normalization both belong to the pack, which the browser does
 * not have — `validate` is a function and cannot cross the wire. So the client
 * sends what the user typed and the server answers with a precise error, using
 * the pack's own `errorKey`. Doing it here would mean compiling one country's
 * rules into the shared contract again, which is the thing this change exists
 * to undo.
 */
export const CreateCounterpartySchema = CounterpartyFieldsSchema;
export type CreateCounterparty = z.infer<typeof CreateCounterpartySchema>;

export const UpdateCounterpartySchema = CounterpartyFieldsSchema.partial();
export type UpdateCounterparty = z.infer<typeof UpdateCounterpartySchema>;

export const CounterpartyQuerySchema = PaginationQuerySchema.extend({
  kind: CounterpartyKindSchema.optional(),
  ivaCondition: IvaConditionSchema.optional(),
  province: z.string().trim().max(16).optional(),
  isActive: z.coerce.boolean().optional(),
});
export type CounterpartyQuery = z.infer<typeof CounterpartyQuerySchema>;
export type CounterpartyQueryInput = z.input<typeof CounterpartyQuerySchema>;

/**
 * A lookup request. The identifier alone is enough: the workspace's country
 * says which registry to ask, and it is the only one that could answer.
 */
export const TaxpayerLookupQuerySchema = z.object({
  taxId: taxIdField,
});
export type TaxpayerLookupQuery = z.infer<typeof TaxpayerLookupQuerySchema>;
